/**
 * The rate-card form (POST / PATCH /wireline-slickline/rates).
 *
 * Currency, the depth unit and the distance unit are NEVER sent — they are the
 * company's, stamped by the server (`currency_not_settable`,
 * `unit_not_settable`); they are shown read-only. The fields that apply to one
 * kind only are shown — and sent — for that kind only:
 *
 *   unit                         a wireline unit (optional; null = any unit)
 *                                (`unit_id_not_unit_kind`)
 *   crew / subsistence           a crew position         (`crew_position_not_people`)
 *   unit per run, depth, shots,  a run kind (optional)   (`run_kind_not_per_run`)
 *     guns, plugs
 *   per depth                    the depth basis *       (`depth_basis_not_per_depth`)
 *   per shot                     the shot basis *        (`shot_basis_not_per_shot`)
 *   third_party                  NO RATE — `pricing` at cost or a markup %; the
 *                                vendor's cost is entered on each ticket line
 *                                (`third_party_rate`, `markup_not_markup`)
 *
 * The unit picker offers only the units the API accepts for the kind (a copy
 * of its WL_KIND_UNITS, used to narrow the list); the API still decides, and a
 * pair it refuses is said (`unit_not_for_kind`).
 */
import React, { useEffect, useState } from 'react';
import { wlBlankToNull, wlToInput } from '../../../lib/wireline-slickline';
import { wlCrewPositionApplies, wlRunKindApplies, wlUnitsForKind } from '../../../lib/wireline-slickline-tickets';
import type { WlRate, WlRateWrite, WlTicketCatalog } from '../../../types/wireline-slickline-tickets';
import { WL_DATE_INPUT, WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError } from '../WlBits';
import { PfFormGrid as WlFormGrid } from '../../pipeline-facility/digs/DigBits';

export interface WlRateFormOptions {
  customers: { id: string; name: string }[];
  /** The company's wireline units (wl_unit_specs ids). */
  units: { id: string; unitNumber: string }[];
}

export function WlRateForm({
  rate,
  catalog,
  options,
  currency,
  depthUnit,
  distanceUnit,
  today,
  defaultCustomerId,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  rate?: WlRate | null;
  catalog: WlTicketCatalog | null;
  options: WlRateFormOptions;
  currency: string | null;
  /** The tenant's depth and distance units, from /meta — shown, never sent. */
  depthUnit?: string | null;
  distanceUnit?: string | null;
  today: string | null;
  defaultCustomerId?: string;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: WlRateWrite) => void;
}) {
  const [code, setCode] = useState(rate?.code ?? '');
  const [name, setName] = useState(rate?.name ?? '');
  const [kind, setKind] = useState(rate?.kind ?? '');
  const [unit, setUnit] = useState(rate?.unit ?? '');
  const [amount, setAmount] = useState(wlToInput(rate?.rate));
  const [pricing, setPricing] = useState(rate?.pricing ?? '');
  const [markup, setMarkup] = useState(wlToInput(rate?.markupPct));
  const [depthBasis, setDepthBasis] = useState(rate?.depthBasis ?? '');
  const [shotBasis, setShotBasis] = useState(rate?.shotBasis ?? '');
  const [runKind, setRunKind] = useState(rate?.runKind ?? '');
  const [customerId, setCustomerId] = useState(rate ? rate.customerId ?? '' : defaultCustomerId ?? '');
  const [crewPosition, setCrewPosition] = useState(rate?.crewPosition ?? '');
  const [unitId, setUnitId] = useState(rate?.unitId ?? '');
  const [from, setFrom] = useState(rate?.effectiveFrom ?? today ?? '');
  const [to, setTo] = useState(rate?.effectiveTo ?? '');
  const [description, setDescription] = useState(rate?.description ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  // The tenant's day arrives with the rates query's meta, usually after the
  // first render; a new rate starts on it unless the office has picked a day.
  useEffect(() => {
    if (!rate && today) setFrom((f) => f || today);
  }, [rate, today]);

  const thirdParty = kind === 'third_party';
  const people = wlCrewPositionApplies(kind);
  const allowedUnits = wlUnitsForKind(kind);
  const unitChoices = (catalog?.priceUnits ?? []).filter((u) => !allowedUnits || allowedUnits.includes(u.value));
  const runCounted = !!kind && !!unit && wlRunKindApplies(kind, unit);
  // A kind with one possible unit picks it; a kind that no longer allows the
  // picked unit clears it, so the office is not left holding a pair the API refuses.
  const pickKind = (next: string) => {
    setKind(next);
    const allowed = wlUnitsForKind(next);
    if (allowed && allowed.length === 1) setUnit(allowed[0]);
    else if (allowed && !allowed.includes(unit)) setUnit('');
  };

  const submit = () => {
    setProblem(null);
    if (!code.trim()) return setProblem('Enter a short code for the rate.');
    if (!name.trim()) return setProblem('Enter the rate’s name.');
    if (!kind || !unit) return setProblem('Pick what kind of charge it is and its unit.');
    if (thirdParty) {
      if (pricing !== 'at_cost' && pricing !== 'markup_pct') return setProblem('Pick how the vendor’s cost is billed: at cost, or cost plus a markup %.');
      if (pricing === 'markup_pct' && (!markup.trim() || !Number.isFinite(Number(markup)) || Number(markup) < 0))
        return setProblem('Enter the markup as a percentage of 0 or more.');
    } else if (!amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) < 0) {
      return setProblem('Enter the rate as a number of 0 or more.');
    }
    if (unit === 'per_depth' && !depthBasis) return setProblem('Pick what a per-depth rate is counted on: max depth, footage run or the interval.');
    if (unit === 'per_shot' && !shotBasis) return setProblem('Pick what a per-shot rate is counted on: shots fired or shots loaded.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setProblem('Enter the day the rate starts.');
    if (to && to < from) return setProblem('The end day can’t be before the start day.');
    const body: WlRateWrite = {
      code: code.trim(),
      name: name.trim(),
      kind,
      unit,
      effective_from: from,
      effective_to: to || null,
      customer_id: customerId || null,
      description: wlBlankToNull(description),
      notes: wlBlankToNull(notes),
    };
    // A third-party rate carries no rate of its own (`third_party_rate`); it is never sent.
    if (thirdParty) {
      body.pricing = pricing;
      if (pricing === 'markup_pct') body.markup_pct = markup.trim();
    } else {
      body.rate = amount.trim();
    }
    if (unit === 'per_depth') body.depth_basis = depthBasis;
    if (unit === 'per_shot') body.shot_basis = shotBasis;
    if (runCounted) body.run_kind = runKind || null;
    if (people) body.crew_position = crewPosition || null;
    if (kind === 'unit') body.unit_id = unitId || null;
    onSubmit(body);
  };

  return (
    <div data-testid="wl-rate-form" className="space-y-4 px-4 py-4">
      <WlFormError message={problem ?? error} />
      <WlFormGrid>
        <WlField label="Code *" hint="Short, e.g. DEPTH-M. Unique per customer card and period.">
          <input className={WL_INPUT} maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} />
        </WlField>
        <WlField label="Name *">
          <input className={WL_INPUT} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </WlField>
        <WlField label="Card" hint="The default card applies to every customer without a rate of their own.">
          <select className={WL_INPUT} aria-label="Card" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Default card (every customer)</option>
            {options.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Kind *">
          <select className={WL_INPUT} aria-label="Kind" value={kind} onChange={(e) => pickKind(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceKinds ?? []).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Unit *">
          <select className={WL_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="">Pick one</option>
            {unitChoices.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </WlField>
        {thirdParty ? (
          <>
            <WlField label="Pricing *" hint="A third-party rate has no rate of its own: the vendor’s cost is entered on each ticket line.">
              <select className={WL_INPUT} aria-label="Pricing" value={pricing} onChange={(e) => setPricing(e.target.value)}>
                <option value="">Pick one</option>
                {(catalog?.thirdPartyPricing ?? []).map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </WlField>
            {pricing === 'markup_pct' && (
              <WlField label="Markup % *">
                <input className={WL_INPUT} inputMode="decimal" aria-label="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} />
              </WlField>
            )}
          </>
        ) : (
          <WlField label="Rate *">
            <input className={WL_INPUT} inputMode="decimal" aria-label="Rate" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </WlField>
        )}
        <WlField label="Currency" hint="The company’s currency, set in Settings. Not set per rate.">
          <p data-testid="wl-rate-currency" className="flex min-h-11 items-center font-mono text-sm text-white">
            {currency ?? '—'}
          </p>
        </WlField>
        {unit === 'per_depth' && (
          <WlField label="Depth basis *" hint={`Counted in ${depthUnit ?? 'the company’s depth unit'}.`}>
            <select className={WL_INPUT} aria-label="Depth basis" value={depthBasis} onChange={(e) => setDepthBasis(e.target.value)}>
              <option value="">Pick one</option>
              {(catalog?.depthBases ?? []).map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </WlField>
        )}
        {unit === 'per_shot' && (
          <WlField label="Shot basis *">
            <select className={WL_INPUT} aria-label="Shot basis" value={shotBasis} onChange={(e) => setShotBasis(e.target.value)}>
              <option value="">Pick one</option>
              {(catalog?.shotBases ?? []).map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </WlField>
        )}
        {unit === 'per_distance' && (
          <WlField label="Distance unit" hint="The company’s, set in Settings. Not set per rate.">
            <p data-testid="wl-rate-distance-unit" className="flex min-h-11 items-center font-mono text-sm text-white">
              {distanceUnit ?? 'The company’s (km or mi)'}
            </p>
          </WlField>
        )}
        {runCounted && (
          <WlField label="Run kind" hint="Optional. Prices only runs of this kind.">
            <select className={WL_INPUT} aria-label="Run kind" value={runKind} onChange={(e) => setRunKind(e.target.value)}>
              <option value="">Any run</option>
              {(catalog?.runKinds ?? []).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </WlField>
        )}
        {kind === 'unit' && (
          <WlField label="Wireline unit" hint="Optional. Prices one unit; blank prices every unit.">
            <select className={WL_INPUT} aria-label="Wireline unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">Any unit</option>
              {options.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.unitNumber}
                </option>
              ))}
            </select>
          </WlField>
        )}
        {people && (
          <WlField label="Crew position" hint="Optional. Prices the hands on the day sheet in this position.">
            <select className={WL_INPUT} aria-label="Crew position" value={crewPosition} onChange={(e) => setCrewPosition(e.target.value)}>
              <option value="">Any position</option>
              {(catalog?.crewPositions ?? []).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </WlField>
        )}
        <WlField label="Starts *">
          <input className={WL_DATE_INPUT} type="date" aria-label="Starts" value={from} onChange={(e) => setFrom(e.target.value)} />
        </WlField>
        <WlField label="Ends" hint="Blank = no end day.">
          <input className={WL_DATE_INPUT} type="date" aria-label="Ends" value={to} onChange={(e) => setTo(e.target.value)} />
        </WlField>
      </WlFormGrid>
      <WlField label="Description" hint="Optional.">
        <input className={WL_INPUT} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
      </WlField>
      <WlField label="Notes">
        <textarea className={`${WL_INPUT} h-20 py-2`} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={WL_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
